import React from 'react';
import format from 'date-fns/format';
import { useUrlParams } from 'Utils/useUrlParams';
import { filterValueByIncludes } from 'Features/Stations/Station/Datasource/tableUtils';
import { useSelector } from 'react-redux';
import { timeZoneSelector } from 'Features/App/appSlice';
import { useGetAllMeteoCodesQuery } from 'Services/api/meteoCodesApi';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import {
  initialParamsState,
  TIMESERIES_TABLE_TYPE,
  isTrue,
  DIRTY_DATA_TABLE_TYPE,
  DATE_TIME_FORMAT, selectableConfines, REPORT_DATA_TABLE_TYPE,
} from './Timeseries.Utils';

export const TimeseriesContext = React.createContext();

const { Provider } = TimeseriesContext;

export default function TimeseriesProvider({
  addByDatasourcePartitionColumns,
  children,
  sourceId,
  sourceType,
  tableType,
  allMeteocodes,
  posts = [],
  columns = [],
  useGetDataQuery,
  timeZone = 'selectedUserTimeZone',
  reportOptions = {
    calcModuleParams: {
      calc: false,
      round: false,
      includeOffGridData: true,
      factData: false,
      shouldAddByDatasourcePartitionColumns: true
    },
    isFixedPanelParams: false,
    date: {
      startDate: '',
      endDate: '',
    },
    pageSize: '',
  },
}) {
  const { data: meteocodes = [], isFetching } = useGetAllMeteoCodesQuery(undefined, { skip: allMeteocodes.length });

  const [urlParams] = useUrlParams({
    meteocodes: 'array',
    page: 'number',
    confines: 'array',
  });

  const selectedTimeZone = useSelector(timeZoneSelector, (a, b) => a[timeZone] === b[timeZone]);

  const formattedMeteocodes = React.useMemo(() => meteocodes.map((meteocode) => ({
    meteoCode: meteocode.meteoCodeString,
    meteoCodeName: meteocode.name,
    meteoCodeDescription: meteocode.description,
    unit: meteocode.unit,
  })), [meteocodes]);

  const getParams = React.useCallback(() => {
    const factDataEnabled = isTrue(urlParams.factData, 'fact') || (tableType === 'report' && reportOptions.calcModuleParams.factData);

    const defaultParams = {
      allMeteocodes: allMeteocodes.length > 0 ? allMeteocodes : formattedMeteocodes,
      view: (tableType !== DIRTY_DATA_TABLE_TYPE && urlParams.view) || initialParamsState.view,
      meteocodes: allMeteocodes.filter((meteocode) => {
        return meteocode.firstReceivedTimeUTC !== null
      }),
      pageSize: urlParams.pageSize || initialParamsState.pageSize,
      page: urlParams.page || 0,
      sourceType,
      sourceId,
      date: {
        startDate: urlParams.startDate ? format(new Date(urlParams.startDate), DATE_TIME_FORMAT) : initialParamsState.startDate,
        endDate: urlParams.endDate ? format(new Date(urlParams.endDate), DATE_TIME_FORMAT) : initialParamsState.endDate,
      },
      tableType,
      columns: columns.length !== 0 ? columns : [],
      useGetDataQuery,
      posts,
      zone: selectedTimeZone[timeZone],
      confines: urlParams.confines ? selectableConfines.filter((confine) => urlParams.confines.includes(confine.value)) : [],
      calcModuleParams: {
        calc: (!factDataEnabled && isTrue(urlParams.calc, 'calc')) || (tableType === 'report' && reportOptions.calcModuleParams.factData),
        round: isTrue(urlParams.round, 'round') || (tableType === 'report' && reportOptions.calcModuleParams.factData),
        includeOffGridData: urlParams.includeOffGridData ? isTrue(urlParams.includeOffGridData) : true,
        factData: factDataEnabled,
        shouldAddByDatasourcePartitionColumns : addByDatasourcePartitionColumns,
      },
      meteocodeFilter: urlParams.meteocodeFilter || 'entity',
    };

    if (!urlParams.meteocodes) {
    } else if (urlParams.meteocodes.length > 0) {
      defaultParams.meteocodes = allMeteocodes.filter((meteocode) => urlParams.meteocodes.includes(meteocode.selectedName));
    } else {
      defaultParams.meteocodes = allMeteocodes.slice(0, 5);
    }

    // TODO: Для отчета потом сделать также по фильтру метеокодов
    if (columns.length === 0) {
      if (defaultParams.meteocodes.length > 0) {
        defaultParams.columns = defaultParams.meteocodes.map((meteocode) => {
          const object = defaultParams.meteocodeFilter === 'entity'
              ? {stationEntityId: meteocode.entityId || -1}
              : {place: meteocode.placeName || ''};

          return {
            sourceType: defaultParams.sourceType,
            sourceId: defaultParams.sourceId,
            meteocode: meteocode.meteoCode,
            steps: defaultParams.calcModuleParams.factData ? null : meteocode.steps,
            ...object,
          };
        });
      }
    } else {
        // колонки из отчета
        defaultParams.columns = columns.map((column) => ({
          ...column,
          steps: defaultParams.calcModuleParams.factData ? null : column.steps,
        }));
    }

    if (tableType === TIMESERIES_TABLE_TYPE || tableType === REPORT_DATA_TABLE_TYPE) {
      defaultParams.isChartEnabled = true;
      defaultParams.confines = urlParams.confines ? filterValueByIncludes(selectableConfines, urlParams.confines) : [];
    }

    if (tableType === REPORT_DATA_TABLE_TYPE) {
      defaultParams.isFixedPanelParams = reportOptions.isFixedPanelParams;
      if (reportOptions.isFixedPanelParams) {
        defaultParams.pageSize = reportOptions.pageSize;
        defaultParams.date = reportOptions.date;
        defaultParams.calcModuleParams = reportOptions.calcModuleParams;
      }
    }
    return defaultParams;
  }, [allMeteocodes, columns, formattedMeteocodes, posts, reportOptions.calcModuleParams, reportOptions.date,
    reportOptions.isFixedPanelParams, reportOptions.pageSize, selectedTimeZone, sourceId, sourceType, tableType,
    timeZone, urlParams.calc, urlParams.confines, urlParams.endDate, urlParams.factData, urlParams.includeOffGridData,
    urlParams.meteocodeFilter, urlParams.meteocodes, urlParams.page, urlParams.pageSize, urlParams.programs, urlParams.round,
    urlParams.startDate, urlParams.view, useGetDataQuery,
  ]);

  const params = React.useMemo(() => getParams(), [getParams]);

  if (isFetching) return <SuspenseSpinner />;

  return (
    <Provider value={params}>
      {children}
    </Provider>
  );
}
